import type { User } from 'firebase/auth'
import { useEffect, useRef, useState } from 'react'

import { datadogRum } from '@/app/config/datadog'
import { auth } from '@/app/config/firebase'
import { sessionStorage } from '@/shared/utils/sessionStorage'

import { loginWithToken } from '../api/authApi'

import { AuthContext } from './authContextDefinition'

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const loginAttemptedRef = useRef(false)

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async user => {
      const storedUid = sessionStorage.getUid()
      const storedToken = sessionStorage.getToken()

      // Case 1: User is logged in
      if (user) {
        // Check if the logged-in user's UID matches the UID in session storage
        if (storedUid && user.uid !== storedUid) {
          // UID mismatch - check if we have a token to attempt login
          if (storedToken && !loginAttemptedRef.current) {
            loginAttemptedRef.current = true
            setLoading(true)

            try {
              // Sign out the current user first
              await auth.signOut()
              // Attempt to login with the stored token
              await loginWithToken(storedToken)
              // If successful, onAuthStateChanged will be triggered again with new user
              return
            } catch (error) {
              console.error('Auto-login failed:', error)
              // If login fails, clear session and show not logged in screen
              sessionStorage.clear()
              datadogRum.clearUser()
              setUser(null)
              setLoading(false)
              loginAttemptedRef.current = false
              return
            }
          } else {
            // No token or login already attempted - log out
            await auth.signOut()
            sessionStorage.clear()
            datadogRum.clearUser()
            setUser(null)
            setLoading(false)
            loginAttemptedRef.current = false
            return
          }
        }

        // User matches stored UID or no stored UID - proceed normally
        // Clear session storage on successful authentication
        sessionStorage.clear()

        // Set DataDog user identification
        datadogRum.setUser({
          id: user.uid,
          email: user.email || undefined,
          name: user.displayName || undefined,
        })

        setUser(user)
        setLoading(false)
        loginAttemptedRef.current = false
        return
      }

      // Case 2: User is not logged in
      if (!user && storedUid && storedToken && !loginAttemptedRef.current) {
        loginAttemptedRef.current = true
        setLoading(true)

        try {
          // Attempt to login with stored token
          await loginWithToken(storedToken)
          // If successful, onAuthStateChanged will be triggered again with new user
          return
        } catch (error) {
          console.error('Auto-login failed:', error)
          // If login fails, clear session and show not logged in screen
          sessionStorage.clear()
          datadogRum.clearUser()
          setUser(null)
          setLoading(false)
          loginAttemptedRef.current = false
          return
        }
      }

      // No user and no stored credentials - show not logged in screen
      // Clear DataDog user identification
      datadogRum.clearUser()

      setUser(null)
      setLoading(false)
      loginAttemptedRef.current = false
    })

    return unsubscribe
  }, [])

  /**
   * Used internally - remove when portal connected to other portals
   */
  const retriggerAuth = async () => {
    // Reset the login attempt flag
    loginAttemptedRef.current = false

    // If there's a current user, sign them out to trigger auth flow
    if (auth.currentUser) {
      await auth.signOut()
    } else {
      // If no current user, manually trigger the auth flow
      const storedUid = sessionStorage.getUid()
      const storedToken = sessionStorage.getToken()

      if (storedUid && storedToken) {
        setLoading(true)
        try {
          await loginWithToken(storedToken)
        } catch (error) {
          console.error('Auth retrigger failed:', error)
          sessionStorage.clear()
          setUser(null)
          setLoading(false)
        }
      }
    }
  }

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    retriggerAuth,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
